import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useFirebasePhoneAuth } from "@/lib/usePhoneAuth";
import { ShieldCheck, ArrowRight, Lightning } from "@phosphor-icons/react";

export default function SuperAdminLogin() {
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("mobile");
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const { requestOtp, confirmCode, sending, verifying, resendIn, canResend, error, reset } =
    useFirebasePhoneAuth("recaptcha-super-admin");

  const sendOtp = async (e) => {
    e?.preventDefault();
    try {
      await requestOtp(mobile);
      setStep("otp");
      toast.success(`OTP sent to +91 ${mobile}`);
    } catch (err) {
      toast.error(err.message || "Failed to send OTP");
    }
  };

  const resend = async () => {
    if (!canResend) return;
    try { await requestOtp(mobile); toast.success("OTP sent again"); }
    catch (err) { toast.error(err.message || "Could not resend OTP"); }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault();
    setSubmitting(true);
    try {
      const { idToken } = await confirmCode(otp);
      const r = await api.post("/auth/verify-otp", { id_token: idToken, role: "super_admin" });
      const active = r.data.active_role || r.data.user.role;
      if (active !== "super_admin") {
        toast.error("This mobile is not authorized for platform admin access.");
        setSubmitting(false);
        return;
      }
      login(r.data.token, r.data.user, active);
      toast.success("Welcome, Admin");
      reset();
      nav("/super-admin");
    } catch (err) {
      toast.error(err.message || err.response?.data?.detail || "Unauthorized");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-900 text-white relative overflow-hidden">
      <div id="recaptcha-super-admin" className="fixed bottom-0 left-0 pointer-events-none" />
      <div className="absolute inset-0 opacity-40 pointer-events-none">
        <div className="absolute top-10 right-10 w-96 h-96 rounded-full bg-[#F8CB46]/20 blur-3xl" />
        <div className="absolute bottom-10 left-10 w-80 h-80 rounded-full bg-[#15803D]/20 blur-3xl" />
      </div>

      <div className="relative w-full max-w-md fade-up">
        <div className="text-center mb-6">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-[#F8CB46] items-center justify-center mb-4">
            <ShieldCheck weight="fill" size={28} className="text-black" />
          </div>
          <div className="inline-flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-3">
            <Lightning weight="fill" size={12} className="text-[#F8CB46]" />
            Internal access only
          </div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Platform admin</h1>
          <p className="text-gray-400 mt-1 text-sm">
            {step === "mobile" ? "Enter your authorized mobile" : `OTP sent to +91 ${mobile}`}
          </p>
        </div>

        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          {step === "mobile" ? (
            <form onSubmit={sendOtp} className="space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-gray-400">Mobile</label>
                <div className="mt-2 relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-200 font-semibold text-base pointer-events-none select-none border-r border-white/20 pr-3">+91</span>
                  <input data-testid="super-mobile-input"
                    type="tel" inputMode="numeric" autoFocus maxLength={10}
                    className="w-full bg-white/10 rounded-xl pl-[68px] pr-4 py-3 font-mono text-lg text-white outline-none focus:ring-2 focus:ring-[#F8CB46]"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                    placeholder="9999999999" />
                </div>
              </div>
              {error && <div className="text-xs text-red-400">{error}</div>}
              <button type="submit" disabled={sending} data-testid="super-send-otp-btn"
                className="qd-btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {sending ? "Sending…" : "Request OTP"} <ArrowRight weight="bold" size={18} />
              </button>
            </form>
          ) : (
            <form onSubmit={verifyOtp} className="space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-gray-400">6-digit OTP</label>
                <input data-testid="super-otp-input"
                  type="tel" inputMode="numeric" autoFocus maxLength={6}
                  className="mt-2 w-full bg-white/10 rounded-xl px-4 py-3 font-mono text-2xl tracking-[0.5em] text-center text-white outline-none focus:ring-2 focus:ring-[#F8CB46]"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  placeholder="••••••" />
              </div>
              {error && <div className="text-xs text-red-400" data-testid="super-otp-error">{error}</div>}
              <button type="submit" disabled={submitting || verifying} data-testid="super-verify-otp-btn"
                className="qd-btn-primary w-full disabled:opacity-60">
                {submitting || verifying ? "Verifying…" : "Authorize"}
              </button>
              <div className="flex items-center justify-between text-xs text-gray-400">
                <button type="button" className="underline"
                  onClick={() => { reset(); setOtp(""); setStep("mobile"); }} data-testid="super-change-number">
                  Change number
                </button>
                {canResend ? (
                  <button type="button" onClick={resend} disabled={sending}
                    className="text-[#F8CB46] font-bold" data-testid="super-resend-otp">
                    Resend OTP
                  </button>
                ) : (
                  <span data-testid="super-resend-timer">Resend in {resendIn}s</span>
                )}
              </div>
            </form>
          )}
        </div>

        <div className="text-center mt-4 text-[10px] text-gray-500 uppercase tracking-widest">
          Unauthorized access is logged.
        </div>
      </div>
    </div>
  );
}
